import BackDropComponent from "@/components/BackDrop";
import { approveRechargeRequest, approveRechargeReset, getCompanyInfoRequest, getPendingRechargeRequestsRequest, getUserInfoRequest, rejectRechargeRequest, rejectRechargeReset, } from "@/redux/actions";
import { REMITTANCE_SYSTEM_USER_ID, REMITTANCE_SYSTEM_USER_NAME } from "@/utils/Constants";
import { Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography, } from "@mui/material";
import PendingActionsOutlined from "@mui/icons-material/PendingActionsOutlined";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import "../../../scss/main.scss";
import "../TenantManager/TenantManager.scss";
// System/auto-generated recharge requests are raised under this fixed user id,
// irrespective of tenant — this page surfaces only those, across all tenants.
const SYSTEM_REQUESTED_BY = REMITTANCE_SYSTEM_USER_ID;
const formatChargeType = (type) => (type ? type.replace(/([a-z])([A-Z])/g, "$1 $2") : "-");
const formatTimestamp = (value) => {
    if (!value)
        return "-";
    const ms = Number(value);
    if (Number.isNaN(ms))
        return value;
    return new Date(ms).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};
const RemittanceApprovalsPage = () => {
    var _a, _b, _c, _d;
    const dispatch = useDispatch();
    const [isBackOpenOpen, setIsBackOpenOpen] = React.useState(false);
    const [pendingPage, setPendingPage] = React.useState(0);
    const [pendingPageSize, setPendingPageSize] = React.useState(10);
    const [rejectDialogOpen, setRejectDialogOpen] = React.useState(false);
    const [rejectTargetId, setRejectTargetId] = React.useState("");
    const [rejectReason, setRejectReason] = React.useState("");
    const [approveTargetId, setApproveTargetId] = React.useState("");
    const [approveConfirmOpen, setApproveConfirmOpen] = React.useState(false);
    const PendingRechargeRequestsState = useSelector((state) => state.getPendingRechargeRequestsReducer);
    const ApproveRechargeState = useSelector((state) => state.approveRechargeReducer);
    const RejectRechargeState = useSelector((state) => state.rejectRechargeReducer);
    const UserInfoState = useSelector((state) => state.getUserInfoReducer);
    const CompanyInfoState = useSelector((state) => state.getCompanyInfoReducer);
    const refreshPendingRequests = React.useCallback(() => {
        dispatch(getPendingRechargeRequestsRequest({
            requestedBy: SYSTEM_REQUESTED_BY,
            page: pendingPage,
            size: pendingPageSize,
        }));
    }, [dispatch, pendingPage, pendingPageSize]);
    React.useEffect(() => {
        refreshPendingRequests();
    }, [refreshPendingRequests]);
    // Approve
    React.useEffect(() => {
        if (ApproveRechargeState.data) {
            enqueueSnackbar("Recharge request approved.", { variant: "success" });
            dispatch(approveRechargeReset());
            setIsBackOpenOpen(false);
            refreshPendingRequests();
        }
        else if (ApproveRechargeState.error) {
            setIsBackOpenOpen(false);
            enqueueSnackbar(ApproveRechargeState.error, { variant: "error" });
            dispatch(approveRechargeReset());
        }
    }, [ApproveRechargeState]);
    // Reject
    React.useEffect(() => {
        if (RejectRechargeState.data) {
            enqueueSnackbar("Recharge request rejected.", { variant: "success" });
            dispatch(rejectRechargeReset());
            setIsBackOpenOpen(false);
            setRejectDialogOpen(false);
            setRejectReason("");
            setRejectTargetId("");
            refreshPendingRequests();
        }
        else if (RejectRechargeState.error) {
            setIsBackOpenOpen(false);
            enqueueSnackbar(RejectRechargeState.error, { variant: "error" });
            dispatch(rejectRechargeReset());
        }
    }, [RejectRechargeState]);
    const pendingRequests = (_b = (_a = PendingRechargeRequestsState === null || PendingRechargeRequestsState === void 0 ? void 0 : PendingRechargeRequestsState.data) === null || _a === void 0 ? void 0 : _a.content) !== null && _b !== void 0 ? _b : [];
    const pendingTotal = (_d = (_c = PendingRechargeRequestsState === null || PendingRechargeRequestsState === void 0 ? void 0 : PendingRechargeRequestsState.data) === null || _c === void 0 ? void 0 : _c.totalElements) !== null && _d !== void 0 ? _d : 0;
    // Tracks every userId ever requested this session so a repeated id never triggers a second network call.
    const requestedUserIdsRef = React.useRef(new Set());
    React.useEffect(() => {
        const ids = new Set();
        pendingRequests.forEach((item) => item.requestedBy && item.requestedBy !== SYSTEM_REQUESTED_BY && ids.add(item.requestedBy));
        ids.forEach((id) => {
            if (requestedUserIdsRef.current.has(id))
                return;
            requestedUserIdsRef.current.add(id);
            dispatch(getUserInfoRequest({ userId: id }));
        });
    }, [pendingRequests, dispatch]);
    const getUserDisplayName = React.useCallback((userId) => {
        var _a, _b;
        if (!userId)
            return "-";
        if (userId === SYSTEM_REQUESTED_BY)
            return REMITTANCE_SYSTEM_USER_NAME;
        const info = UserInfoState.map[userId];
        if (info) {
            const name = `${(_a = info.firstName) !== null && _a !== void 0 ? _a : ""} ${(_b = info.lastName) !== null && _b !== void 0 ? _b : ""}`.trim();
            return name || userId;
        }
        if (UserInfoState.failedIds[userId])
            return userId;
        return "Loading…";
    }, [UserInfoState]);
    // Tracks every tenantId ever requested this session so a repeated id never triggers a second network call.
    const requestedCompanyIdsRef = React.useRef(new Set());
    React.useEffect(() => {
        const missingIds = new Set();
        pendingRequests.forEach((item) => {
            const tenantId = item.tenantId;
            if (tenantId && !requestedCompanyIdsRef.current.has(tenantId))
                missingIds.add(tenantId);
        });
        if (missingIds.size === 0)
            return;
        missingIds.forEach((id) => requestedCompanyIdsRef.current.add(id));
        dispatch(getCompanyInfoRequest({ companyIds: Array.from(missingIds) }));
    }, [pendingRequests, dispatch]);
    const getCompanyDisplayName = React.useCallback((tenantId) => {
        if (!tenantId)
            return "-";
        const info = CompanyInfoState.map[tenantId];
        if (info)
            return info.name || tenantId;
        if (CompanyInfoState.failedIds[tenantId])
            return tenantId;
        return "Loading…";
    }, [CompanyInfoState]);
    const handleApproveClick = (requestId) => {
        setApproveTargetId(requestId);
        setApproveConfirmOpen(true);
    };
    const handleConfirmApprove = () => {
        setApproveConfirmOpen(false);
        setIsBackOpenOpen(true);
        dispatch(approveRechargeRequest({ requestId: approveTargetId }));
    };
    const handleRejectClick = (requestId) => {
        setRejectTargetId(requestId);
        setRejectReason("");
        setRejectDialogOpen(true);
    };
    const handleConfirmReject = () => {
        if (!rejectReason.trim())
            return;
        setIsBackOpenOpen(true);
        dispatch(rejectRechargeRequest({ requestId: rejectTargetId, reason: rejectReason.trim() }));
    };
    return (React.createElement(Grid, { container: true, direction: "column", wrap: "nowrap", sx: { p: 5, minHeight: "calc(100vh - 66px)", minWidth: 0, overflowX: "hidden" }, className: "m_background_color" },
        React.createElement(BackDropComponent, { open: isBackOpenOpen }),
        React.createElement(Grid, { item: true, mb: 3, mt: 3 },
            React.createElement(Typography, { sx: {
                    fontFamily: "Poppins",
                    fontSize: "20px",
                    fontWeight: 600,
                    lineHeight: "30px",
                    textAlign: "left",
                    color: "#121114",
                } }, "Remittance Approvals"),
            React.createElement(Typography, { sx: { fontFamily: "Poppins", fontSize: 13, color: "#64748B", mt: 0.5 } }, "System-raised wallet recharge requests awaiting approver sign-off, across all tenants.")),
        React.createElement(Grid, { item: true, sx: { minWidth: 0 } },
            React.createElement(Card, { variant: "outlined", className: "tm_card", sx: { mb: 2, borderLeft: "4px solid #F59E0B" } },
                React.createElement(CardContent, null,
                    React.createElement(Box, { display: "flex", alignItems: "center", gap: 1.5, mb: 1 },
                        React.createElement(Box, { sx: { width: 34, height: 34, borderRadius: "9px", backgroundColor: "#FFFBEB", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 } },
                            React.createElement(PendingActionsOutlined, { sx: { fontSize: 18, color: "#D97706" } })),
                        React.createElement(Box, null,
                            React.createElement(Typography, { className: "tm_section_title" }, "Remittance Approvals"),
                            React.createElement(Typography, { sx: { fontFamily: "Poppins", fontSize: 11, color: "#64748B", lineHeight: 1.4 } },
                                "Requested by system user ",
                                REMITTANCE_SYSTEM_USER_NAME))),
                    React.createElement(Divider, { className: "tm_divider" }),
                    React.createElement(Paper, { variant: "outlined", sx: { width: "100%", overflowX: "auto", mt: 1.5, border: "none" } },
                        React.createElement(TableContainer, null,
                            React.createElement(Table, { size: "small", sx: { minWidth: 720 } },
                                React.createElement(TableHead, null,
                                    React.createElement(TableRow, null,
                                        React.createElement(TableCell, { sx: { fontFamily: "Poppins", fontSize: 12, fontWeight: 600, color: "#64748B" } }, "Requested At"),
                                        React.createElement(TableCell, { sx: { fontFamily: "Poppins", fontSize: 12, fontWeight: 600, color: "#64748B" } }, "Tenant"),
                                        React.createElement(TableCell, { sx: { fontFamily: "Poppins", fontSize: 12, fontWeight: 600, color: "#64748B" } }, "Type"),
                                        React.createElement(TableCell, { sx: { fontFamily: "Poppins", fontSize: 12, fontWeight: 600, color: "#64748B" }, align: "right" }, "Amount"),
                                        React.createElement(TableCell, { sx: { fontFamily: "Poppins", fontSize: 12, fontWeight: 600, color: "#64748B" } }, "Requested By"),
                                        React.createElement(TableCell, { sx: { fontFamily: "Poppins", fontSize: 12, fontWeight: 600, color: "#64748B" }, align: "right" }, "Actions"))),
                                React.createElement(TableBody, null,
                                    pendingRequests.length === 0 && (React.createElement(TableRow, null,
                                        React.createElement(TableCell, { colSpan: 6, align: "center", sx: { fontFamily: "Poppins", fontSize: 13, color: "#94A3B8", py: 3 } }, PendingRechargeRequestsState.loading ? "Loading…" : "No pending recharge requests."))),
                                    pendingRequests.map((item) => (React.createElement(TableRow, { key: item.requestId },
                                        React.createElement(TableCell, { sx: { fontFamily: "Poppins", fontSize: 12.5 } }, formatTimestamp(item.requestedAt)),
                                        React.createElement(TableCell, { sx: { fontFamily: "Poppins", fontSize: 12.5 } }, getCompanyDisplayName(item.tenantId)),
                                        React.createElement(TableCell, { sx: { fontFamily: "Poppins", fontSize: 12.5 } }, formatChargeType(item.chargeType)),
                                        React.createElement(TableCell, { align: "right", sx: { fontFamily: "Poppins", fontSize: 12.5, fontWeight: 600 } },
                                            "\u20B9",
                                            item.amount),
                                        React.createElement(TableCell, { sx: { fontFamily: "Poppins", fontSize: 12.5 } }, getUserDisplayName(item.requestedBy)),
                                        React.createElement(TableCell, { align: "right" },
                                            React.createElement(Box, { display: "flex", gap: 1, justifyContent: "flex-end" },
                                                React.createElement(Button, { size: "small", variant: "contained", className: "tm_btn_success", sx: { textTransform: "none", minWidth: 84 }, onClick: () => handleApproveClick(item.requestId) }, "Approve"),
                                                React.createElement(Button, { size: "small", variant: "outlined", className: "tm_btn_danger", sx: { textTransform: "none", minWidth: 78 }, onClick: () => handleRejectClick(item.requestId) }, "Reject")))))))))),
                    React.createElement(TablePagination, { component: "div", count: pendingTotal, page: pendingPage, onPageChange: (_, newPage) => setPendingPage(newPage), rowsPerPage: pendingPageSize, onRowsPerPageChange: (e) => {
                            setPendingPageSize(parseInt(e.target.value, 10));
                            setPendingPage(0);
                        }, rowsPerPageOptions: [10, 20, 50], sx: { fontFamily: "Poppins" } })))),
        React.createElement(Dialog, { open: approveConfirmOpen, onClose: () => setApproveConfirmOpen(false), maxWidth: "xs", fullWidth: true },
            React.createElement(DialogTitle, { sx: { fontFamily: "Poppins", fontWeight: 600, fontSize: 16 } }, "Approve Recharge Request"),
            React.createElement(DialogContent, null,
                React.createElement(DialogContentText, { sx: { fontFamily: "Poppins", fontSize: 14 } }, "This will credit/debit the tenant's wallet immediately. This action cannot be undone.")),
            React.createElement(DialogActions, { sx: { px: 3, pb: 2, gap: 1 } },
                React.createElement(Button, { onClick: () => setApproveConfirmOpen(false), variant: "outlined", sx: { textTransform: "none", fontFamily: "Poppins", borderRadius: "8px", borderColor: "#CBD5E1", color: "#64748B" } }, "Cancel"),
                React.createElement(Button, { onClick: handleConfirmApprove, variant: "contained", className: "tm_btn_success", sx: { textTransform: "none" } }, "Confirm Approve"))),
        React.createElement(Dialog, { open: rejectDialogOpen, onClose: () => setRejectDialogOpen(false), maxWidth: "xs", fullWidth: true },
            React.createElement(DialogTitle, { sx: { fontFamily: "Poppins", fontWeight: 600, fontSize: 16 } }, "Reject Recharge Request"),
            React.createElement(DialogContent, null,
                React.createElement(DialogContentText, { sx: { fontFamily: "Poppins", fontSize: 14, mb: 1.5 } }, "Please provide a reason for rejecting this request."),
                React.createElement(TextField, { fullWidth: true, multiline: true, minRows: 3, size: "small", placeholder: "Rejection reason", value: rejectReason, onChange: (e) => setRejectReason(e.target.value), sx: { "& .MuiOutlinedInput-root": { borderRadius: "8px" } } })),
            React.createElement(DialogActions, { sx: { px: 3, pb: 2, gap: 1 } },
                React.createElement(Button, { onClick: () => setRejectDialogOpen(false), variant: "outlined", sx: { textTransform: "none", fontFamily: "Poppins", borderRadius: "8px", borderColor: "#CBD5E1", color: "#64748B" } }, "Cancel"),
                React.createElement(Button, { onClick: handleConfirmReject, variant: "contained", className: "tm_btn_danger", disabled: !rejectReason.trim(), sx: { textTransform: "none", border: "1px solid #EF4444" } }, "Confirm Reject")))));
};
export default RemittanceApprovalsPage;
